/**
 * The count shown against a sidebar item, or nothing.
 *
 * Polaris renders whatever string a nav item's `badge` is given, so the decision NOT to show
 * one has to be made here — there is no falsy shortcut that hides a zero. Three states, and
 * two of them show nothing:
 *
 *   a positive number  the badge, as a string
 *   zero               nothing. A badge that is always present is one nobody reads, and the
 *                      state it would announce is "there is nothing to do".
 *   anything else      nothing. Still loading, or the request failed — and a count nobody can
 *                      vouch for should not appear beside a number people act on.
 *
 * Separate from the component because it is a rule rather than a render: the badge is the only
 * thing in the console that says a brand is waiting to be let in, so when it appears matters
 * more than how it looks.
 */

/**
 * @param {unknown} count whatever the count query returned, including nothing
 * @returns {string|undefined} the badge text, or undefined to render no badge at all
 */
export function countBadge(count) {
  // Number() rather than a truthy test: the API answers { count }, and a numeric string is a
  // perfectly ordinary thing for an API to send. Number(null) is 0 and Number(undefined) is
  // NaN, both of which fall through to undefined below, which is what we want for each.
  const n = Number(count);

  if (!Number.isFinite(n) || n <= 0) return undefined;

  // Floored, not rounded: 1.5 pending requests is not a thing, and if a count ever arrives
  // fractional it is a bug upstream that should not be dressed up as a clean number here.
  return String(Math.floor(n));
}
